import PropTypes from 'prop-types';
import React, { useState, useMemo, useCallback } from 'react';
import {
  RiCheckLine,
  RiCloseLine,
  RiInformationLine,
  RiErrorWarningLine,
  RiArrowUpSLine,
  RiArrowDownSLine,
} from 'react-icons/ri';
import { t } from 'ttag';

function Alert({
  children,
  dataTest,
  expandableText,
  isClosable,
  isExpandable,
  showIcon,
  onClose,
  variant,
}) {
  const [isExpanded, toggleExpanded] = useState(false);

  const divClassNames = useMemo(() => {
    const classNames = [
      'flex',
      'items-center',
      'justify-between',
      'border',
      'border-solid',
      'space-x-4',
      'py-4',
      'px-8',
      isExpandable ? 'hover:cursor-pointer' : 'hover:cursor-default',
    ];

    switch (variant) {
      case 'warning':
        classNames.push('border-[#FAEBCC]', 'bg-[#FCF8E3]');
        break;
      case 'error':
        classNames.push('border-[#DB3F39]', 'bg-[#DF6C4F]');
        break;
      case 'success':
        classNames.push('border-[#D6E9C6]', 'bg-[#DFF0D8]');
        break;
      case 'info':
        classNames.push('border-[#ACE5E7]', 'bg-[#C8E7EF]');
        break;
      default:
        classNames.push('border-[#ACE5E7]', 'bg-[#C8E7EF]');
        break;
    }

    return classNames.join(' ');
  }, [isExpandable, variant]);

  const getTextColor = useCallback(() => {
    switch (variant) {
      case 'warning':
        return 'text-[#8A6D3B]';
      case 'error':
        return 'text-white';
      case 'success':
        return 'text-[#2C572D]';
      case 'info':
        return 'text-dark-gray';
      default:
        return 'text-dark-gray';
    }
  }, [variant]);

  const pClassNames = useMemo(
    () => ['text-sm', 'leading-none', getTextColor()].join(' '),
    [variant]
  );

  const iconClassNames = useMemo(
    () => ['h-4', 'w-4', 'shrink-0', getTextColor()].join(' '),
    [variant]
  );

  const getIcon = useCallback(() => {
    switch (variant) {
      case 'warning':
        return <RiErrorWarningLine className={iconClassNames} />;
      case 'error':
        return <RiErrorWarningLine className={iconClassNames} />;
      case 'success':
        return <RiCheckLine className={iconClassNames} />;
      case 'info':
        return <RiInformationLine className={iconClassNames} />;
      default:
        return <RiInformationLine className={iconClassNames} />;
    }
  }, [variant]);

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <div
      className={divClassNames}
      role="alert"
      onClick={isExpandable ? () => toggleExpanded(!isExpanded) : null}
      data-testid={dataTest}
    >
      <div className="flex items-center space-x-4">
        {showIcon && getIcon()}
        <p className={pClassNames}>{isExpanded ? expandableText : children}</p>
      </div>
      {isClosable && !isExpandable && (
        <button
          className="m-0 flex items-center border-0 bg-transparent p-0 hover:cursor-pointer"
          aria-label={t`Close Alert`}
          type="button"
          onClick={onClose}
        >
          <RiCloseLine className={iconClassNames} />
        </button>
      )}

      {isExpandable && (
        <div className="flex items-center">
          {isExpanded ? (
            <RiArrowUpSLine className={iconClassNames} />
          ) : (
            <RiArrowDownSLine className={iconClassNames} />
          )}
        </div>
      )}
    </div>
  );
}

Alert.propTypes = {
  children: PropTypes.string.isRequired,
  dataTest: PropTypes.string,
  expandableText: PropTypes.node,
  isClosable: PropTypes.bool,
  isExpandable: PropTypes.bool,
  onClose: PropTypes.func,
  showIcon: PropTypes.bool,
  variant: PropTypes.oneOf(['info', 'warning', 'error', 'success']),
};

Alert.defaultProps = {
  dataTest: 'alert',
  expandableText: undefined,
  isClosable: true,
  isExpandable: false,
  onClose: () => {},
  showIcon: true,
  variant: 'info',
};

export default Alert;
